/**
 * ConfigSection - Configuration section for scroll wizard
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTournament } from '../../contexts/TournamentContext';

const ConfigSection = ({ onResetClick }) => {
  const { config: savedConfig, setConfig: saveConfig } = useTournament();
  const formLayoutRef = useRef(null);
  const [lockedWidth, setLockedWidth] = useState(null);

  const [formData, setFormData] = useState({
    tournamentName: '',
    playerCount: 8,
    courtCount: 2,
    startTimeStr: '09:00',
    totalDuration: 120,
    warmupDuration: 10,
    pauseDuration: 3,
    matchDuration: 19,
    setsToWin: 1,
    gamesPerSet: 6,
    unlimitedGames: true,
    tiebreakPoints: 7,
    allowDraws: true,
    hasFinalPhase: true,
    finalFormula: 'B'
  });

  useEffect(() => {
    if (savedConfig) {
      const needsName = savedConfig.tournamentName == null;
      let tournamentName = savedConfig.tournamentName;
      if (needsName) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        tournamentName = `Tournoi ${day}/${month} ${hours}:${minutes}:${seconds}`;
        saveConfig({ ...savedConfig, tournamentName });
      }
      setFormData(prev => ({
        ...prev,
        ...savedConfig,
        tournamentName,
        unlimitedGames: savedConfig.gamesPerSet === null
      }));
    }
  }, [savedConfig, saveConfig]);

  useEffect(() => {
    if (formLayoutRef.current && lockedWidth === null) {
      setLockedWidth(formLayoutRef.current.offsetWidth);
    }
  }, [lockedWidth]);

  const handleInputChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    saveConfig(newData);
  };

  return (
    <div className="section-inner config-section">
      <div className="section-header">
        <h1>
          <i className="bi bi-gear-fill me-3"></i>
          Configuration
        </h1>
        {onResetClick && (
          <button className="config-new-tournament-btn" onClick={onResetClick}>
            <i className="bi bi-arrow-clockwise"></i>
            <span>Nouveau tournoi</span>
          </button>
        )}
      </div>
      <div className="section-body">
        <div className="material-form">
          <div className="config-form-layout" ref={formLayoutRef} style={lockedWidth ? { minWidth: lockedWidth } : undefined}>

            {/* Nom du tournoi */}
            <div className="form-input-material">
              <input
                type="text"
                id="tournamentName"
                placeholder=" "
                value={formData.tournamentName || ''}
                onChange={(e) => handleInputChange('tournamentName', e.target.value)}
                required
              />
              <label htmlFor="tournamentName">Tournoi</label>
            </div>

            {/* Joueurs + Terrains côte à côte */}
            <div className="config-grid-2">
              <div className="form-input-material">
                <input
                  type="number"
                  id="playerCount"
                  placeholder=" "
                  value={formData.playerCount}
                  onChange={(e) => handleInputChange('playerCount', parseInt(e.target.value))}
                  min={4}
                  max={100}
                  required
                />
                <label htmlFor="playerCount">Joueurs</label>
              </div>
              <div className="form-input-material">
                <input
                  type="number"
                  id="courtCount"
                  placeholder=" "
                  value={formData.courtCount}
                  onChange={(e) => handleInputChange('courtCount', parseInt(e.target.value))}
                  min={1}
                  max={20}
                  required
                />
                <label htmlFor="courtCount">Terrains</label>
              </div>
            </div>

            {/* Durée totale */}
            <div className="form-input-material">
              <input
                type="number"
                id="totalDuration"
                placeholder=" "
                value={formData.totalDuration}
                onChange={(e) => handleInputChange('totalDuration', parseInt(e.target.value))}
                min={10}
                max={480}
                required
              />
              <label htmlFor="totalDuration">Durée totale (min.)</label>
            </div>

            {/* Configuration des temps */}
            <p className="config-group-label">Configuration des temps</p>

            <div className="config-grid-3">
              <div className="form-input-material">
                <input
                  type="number"
                  id="warmupDuration"
                  placeholder=" "
                  value={formData.warmupDuration}
                  onChange={(e) => handleInputChange('warmupDuration', parseInt(e.target.value))}
                  min={0}
                  max={60}
                />
                <label htmlFor="warmupDuration">Échauff.</label>
              </div>
              <div className="form-input-material">
                <input
                  type="number"
                  id="pauseDuration"
                  placeholder=" "
                  value={formData.pauseDuration}
                  onChange={(e) => handleInputChange('pauseDuration', parseInt(e.target.value))}
                  min={0}
                  max={30}
                />
                <label htmlFor="pauseDuration">Pause</label>
              </div>
              <div className="form-input-material">
                <input
                  type="number"
                  id="matchDuration"
                  placeholder=" "
                  value={formData.matchDuration}
                  onChange={(e) => handleInputChange('matchDuration', parseInt(e.target.value))}
                  min={5}
                  max={120}
                />
                <label htmlFor="matchDuration">Match</label>
              </div>
            </div>

            {/* Phase finale */}
            <div className="config-toggle-row">
              <input
                type="checkbox"
                id="hasFinalPhase"
                className="config-toggle-checkbox"
                checked={formData.hasFinalPhase !== false}
                onChange={(e) => handleInputChange('hasFinalPhase', e.target.checked)}
              /><label className="config-toggle-label" htmlFor="hasFinalPhase">Phase finale</label>
            </div>

            {/* Phase finale — description de la formule */}
            {formData.hasFinalPhase !== false && (
              <p className="final-formula-desc text-center mb-0">
                <i className="bi bi-info-circle me-1"></i>
                2 meilleurs gauchers &amp; 2 meilleurs droitiers
              </p>
            )}

            {/* Résumé du temps */}
            {(() => {
              const roundCount = savedConfig?.roundCount || 0;
              const hasFinal = formData.hasFinalPhase !== false;
              const totalUsed = formData.warmupDuration
                + roundCount * (formData.matchDuration + formData.pauseDuration)
                + (hasFinal ? formData.matchDuration : 0);
              return (
                <div className="config-time-summary">
                  {formData.warmupDuration > 0 && (
                    <span>{formData.warmupDuration} min échauffement + </span>
                  )}
                  <span>
                    {roundCount} tours × {formData.matchDuration} min + {roundCount} × {formData.pauseDuration} min de pause
                    {hasFinal && ` + phase finale ${formData.matchDuration} min`}
                  </span>
                  <span className="config-time-total"> = {totalUsed} min</span>
                </div>
              );
            })()}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigSection;
